/**
 * Responsibility: own the WebGL rendering pipeline (renderer, scene, camera, and the
 * post-processing chain that produces the neon glow).
 *
 * This is the only module allowed to construct a WebGLRenderer or an EffectComposer.
 * World generation and flight logic never import from here; they hand over plain data
 * and this module turns it into pixels.
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

import { RENDER, BLOOM, degToRad } from '../config.js';

/**
 * Create the rendering pipeline bound to a canvas element.
 *
 * @param {HTMLCanvasElement} canvas
 * @returns {object} Handle exposing the scene, camera, and frame operations.
 */
export function createRenderer(canvas) {
  if (!canvas) {
    throw new Error('createRenderer: a canvas element is required');
  }

  const renderer = new THREE.WebGLRenderer({
    canvas,

    /*
      **Off, because with a post-processing chain it does nothing and is not free.**

      It was on for the right reason — the whole image is thin bright lines against black, the worst case
      for aliasing — but that reasoning belongs to a renderer that draws straight to the canvas. It does
      not. `EffectComposer` builds its own render target with no `samples` (verified in the installed
      source: `new WebGLRenderTarget(w, h, { type: HalfFloatType })`), so the scene is rasterised there
      without multisampling and the only thing ever drawn into the default framebuffer is the output pass's
      fullscreen quad — where multisampling has nothing to do.

      What it did cost is a multisampled default framebuffer at full resolution, which the driver has to
      resolve every single frame for the compositor. That is a whole-screen bandwidth cost per frame in
      exchange for nothing, and it is a prime suspect for a frame time that is constant rather than spiky.
    */
    antialias: false,

    // No transparency needed; the scene always fills the frame with its own background.
    alpha: false,
    powerPreference: 'high-performance',
  });

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, RENDER.MAX_PIXEL_RATIO));

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(RENDER.BACKGROUND_COLOR);

  const camera = new THREE.PerspectiveCamera(
    RENDER.FIELD_OF_VIEW,
    1, // Real aspect ratio is applied by the first setSize() call below.
    RENDER.NEAR_CLIP,
    RENDER.FAR_CLIP,
  );

  /*
    Post-processing chain:
      RenderPass      draws the scene
      UnrealBloomPass extracts bright pixels and blooms them, producing the neon glow
      OutputPass      applies tone mapping and colour-space conversion

    OutputPass must come last. Without it, colours render washed out because the
    composer works in linear space while the display expects sRGB.
  */
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));

  const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(1, 1), // Resolution is set properly by setSize() below.
    BLOOM.STRENGTH,
    BLOOM.RADIUS,
    BLOOM.THRESHOLD,
  );

  bloomPass.enabled = BLOOM.STRENGTH > 0;
  composer.addPass(bloomPass);
  composer.addPass(new OutputPass());

  /**
   * Resize the renderer, composer, and camera to match a viewport size.
   * Called once at startup and again on every window resize.
   *
   * @param {number} width - CSS pixels.
   * @param {number} height - CSS pixels.
   */
  function setSize(width, height) {
    // Guard against a zero height, which happens transiently on some mobile browsers
    // and would make the aspect ratio non-finite.
    const safeWidth = Math.max(1, Math.floor(width));
    const safeHeight = Math.max(1, Math.floor(height));

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, RENDER.MAX_PIXEL_RATIO));
    renderer.setSize(safeWidth, safeHeight, false);
    composer.setSize(safeWidth, safeHeight);

    /*
      Bloom deliberately runs at a fraction of the frame.

      `composer.setSize` has just given it the full frame, and `UnrealBloomPass` then halves that for its
      first mip and halves again four more times — so the two largest of its eleven fullscreen passes are the
      expensive ones and they scale with this number squared. **It is a blur.** Running its input at half
      resolution quarters the area of every level for a difference that is close to invisible on a glow, and
      it is the cheapest large saving available in the whole pipeline.
    */
    bloomPass.setSize(
      Math.max(1, Math.floor(safeWidth * BLOOM.RESOLUTION_SCALE)),
      Math.max(1, Math.floor(safeHeight * BLOOM.RESOLUTION_SCALE)),
    );

    camera.aspect = safeWidth / safeHeight;
    camera.updateProjectionMatrix();
  }

  setSize(window.innerWidth, window.innerHeight);

  return {
    scene,
    camera,

    /** Draw one frame through the post-processing chain. */
    render() {
      /*
        **Counted across the whole chain, not just the last pass of it.** Three.js clears these counters at the
        start of every `renderer.render` call, and a composer makes one such call per pass — so the readout was
        showing `1`, the fullscreen quad of the output pass, while two hundred tiles went unreported. Reset
        once here instead, and the number means what it says.
      */
      renderer.info.autoReset = false;
      renderer.info.reset();

      composer.render();
    },

    setSize,

    /**
     * Adjust glow strength at runtime. Exposed because bloom is the most expensive pass
     * in the pipeline and needs to be dialable on weaker hardware.
     *
     * @param {number} strength
     */
    setBloomStrength(strength) {
      bloomPass.strength = strength;

      /*
        **At zero the pass is removed from the chain, not merely turned down.** A strength of zero still ran
        eleven fullscreen passes to produce black and add it to the image, so the one setting a player would
        reach for on slow hardware bought them nothing. It also makes the slider a live experiment: sliding to
        zero and watching the frame time is the quickest way to find out how much of a frame bloom is costing.
      */
      bloomPass.enabled = strength > 0;
    },

    /** Vertical field of view in radians. Useful for framing calculations. */
    getVerticalFovRadians() {
      return degToRad(camera.fov);
    },

    /**
     * Draw calls submitted for the last frame.
     *
     * For the frame profiler, and the reason it is worth exposing: if frames are being lost while our own
     * work is quick, the next question is whether the GPU is being asked for too much — and draw call count
     * is the cheapest available proxy for that. Read straight off the renderer's own counter, so it costs
     * nothing when nobody asks.
     */
    getDrawCalls() {
      return renderer.info.render.calls;
    },

    /**
     * How much the GPU is actually being asked to fill, and how much of it is bloom.
     *
     * **The number that was missing while a constant 25 ms frame was being diagnosed.** Frame time on a
     * wireframe scene is dominated by pixels rather than by geometry, and pixel count is invisible from the
     * outside: a 1080p display and a HiDPI laptop reporting the same frame time mean completely different
     * things. Reported to the readout so one screenshot answers it.
     *
     * @returns {{width: number, height: number, pixelRatio: number, drawCalls: number, bloom: boolean}}
     *   Width and height are the real framebuffer, pixel ratio already applied.
     */
    getRenderInfo() {
      const size = renderer.getDrawingBufferSize(new THREE.Vector2());

      return {
        width: size.x,
        height: size.y,
        pixelRatio: renderer.getPixelRatio(),
        drawCalls: renderer.info.render.calls,
        bloom: bloomPass.enabled,
      };
    },

    /** Release GPU resources. Called on teardown. */
    dispose() {
      composer.dispose();
      renderer.dispose();
    },
  };
}
